<template>
  <div
    ref="containerElement"
    class="teaser-featured-projects"
    data-horizontal-module
    data-transition-module
  >
    <div class="projects" ref="projectsElement">
      <NuxtLink
        v-for="project in data.featuredProjects"
        :key="project.id"
        :to="buildProjectRoute(project)"
        custom
        v-slot="{ href }"
      >
        <a
          class="project"
          ref="projectElement"
          data-project
          :href="href"
          @click.prevent="
            (event) => goToProject(buildProjectRoute(project), event)
          "
        >
          <figure
            class="figure"
            :class="{
              '-active': active === project.id,
            }"
          >
            <NakedImage
              v-if="project.image?.imageLandscape?.responsiveImage"
              :data="project.image.imageLandscape.responsiveImage"
              picture-class="image _hidden-vs"
            />
            <NakedImage
              v-if="project.image?.imagePortrait?.responsiveImage"
              :data="project.image.imagePortrait.responsiveImage"
              picture-class="image _hidden-vl"
            />

            <figcaption class="caption">
              <h2 class="title text-md">
                <span>{{ project.longTitle }}</span>
              </h2>
              <small class="location text-sm">
                <span>{{ project.location }}</span>
              </small>
            </figcaption>
          </figure>
        </a>
      </NuxtLink>
    </div>
    <div class="cta">
      <NuxtLink class="link button-primary text-md" :to="linkProjects">
        {{ t('allProjects') }}
        <span class="arrow"></span>
      </NuxtLink>
    </div>
  </div>
</template>

<i18n lang="yaml">
de:
  allProjects: Alle Referenzen
fr:
  allProjects: Tous les projets
</i18n>

<script setup lang="ts">
import gsap from 'gsap';
import { NakedImage } from 'vue-datocms';
import type { RouteLocationRaw } from 'vue-router';
import type { TeaserFeaturedProjectsFragment } from '#gql';
import { NuxtLink } from '#components';
import { CustomEase } from 'gsap/CustomEase';
import { useDebounceFn } from '@vueuse/core';

// Register gsap plugin
gsap.registerPlugin(CustomEase);

// Initialize hooks
const { locale, t } = useI18n();
const active = useState();
const router = useRouter();
const { isVl } = useViewportSize();

// Initialize props
const props = defineProps<{
  data: TeaserFeaturedProjectsFragment;
  horizontalScrollTimeline: gsap.core.Timeline;
}>();

// Initialize elements
const containerElement = ref<HTMLElement | null>(null);
const projectsElement = ref<HTMLElement | null>(null);
const projectElement = ref<HTMLElement[]>([]);

// Create link to projects page
const linkProjects = computed(function () {
  if (locale.value === 'fr') {
    return '/fr/projets';
  }
  return '/de/referenzen';
});

// Initialize timelines and tweens
const imageTimelines = ref<gsap.core.Timeline[]>([]);
const imageElements = ref<HTMLElement[]>([]);
const captionTimelines = ref<gsap.core.Timeline[]>([]);
const captionElements = ref<HTMLElement[]>([]);

// Add horizontal animation when viewport size changes
onMounted(async function () {
  await nextTick();

  // Add horizontal animation
  if (isVl.value && containerElement.value) {
    addHorizontalAnimation(props.horizontalScrollTimeline);
  }

  // Add resize handler
  window.addEventListener('resize', resizeHandler);
});

// Remove resize handler
onUnmounted(() => {
  window.removeEventListener('resize', resizeHandler);
});

// Functions
const resizeHandler = useDebounceFn(() => {
  removeHorizontalAnimation();
  if (isVl.value && containerElement.value) {
    addHorizontalAnimation(props.horizontalScrollTimeline);
  }
}, 250);

function removeHorizontalAnimation() {
  imageTimelines.value.forEach((tl) => {
    tl.kill();
  });
  captionTimelines.value.forEach((tl) => {
    tl.kill();
  });
  imageElements.value.forEach((el) => {
    gsap.set(el, { clearProps: 'all' });
  });
  captionElements.value.forEach((el) => {
    gsap.set(el, { clearProps: 'all' });
  });
}

function addHorizontalAnimation(masterTl: gsap.core.Timeline) {
  // Loop through projects and add animations
  projectElement.value.forEach((el, index) => {
    projectScrollAnimation(el, masterTl, index);
  });
}

function buildProjectRoute(
  project: TeaserFeaturedProjectsFragment['featuredProjects'][number],
) {
  return {
    name: `projects-slug___${locale.value}`,
    params: { slug: project.translatedSlug },
  };
}

function goToProject(route: RouteLocationRaw, event: Event) {
  if (!isVl.value) {
    router.push(route);
    return;
  }

  const targetElement = event.target as HTMLElement;
  const projectElement = targetElement.closest('.project') as HTMLElement;
  const projectImageElement = projectElement.querySelector(
    '.image',
  ) as HTMLElement;
  const projectAssetElement = projectImageElement.querySelector(
    'img',
  ) as HTMLElement;

  projectElement.setAttribute('data-transition-project', 'true');

  const computedStyle = window.getComputedStyle(projectAssetElement);
  const transform = computedStyle.transform;
  const scaleValues = getScaleFromMatrix(transform);

  window.PageTransitionData = {
    featuredProjectRect: projectImageElement.getBoundingClientRect(),
    featuredProjectAssetScale: scaleValues,
  };

  router.push(route);
}

function calculateYOffset(index: number, offset: number): number {
  if (index === 0) {
    return 0;
  }

  return offset;
}

function calcShift(index: number, projectEndWidth: number) {
  if (index === 0) {
    return 0;
  }

  // The shift is calculated heuristically
  return (projectEndWidth / index) * (0.075 / index);
}
function projectScrollAnimation(
  el: HTMLElement,
  masterTl: gsap.core.Timeline,
  index: number,
) {
  // Settings
  const projectStartTransform = {
    width: 0.25,
    height: 0.5,
    scale: 1.5,
  };
  const yOffsetPercentage = 30;
  const captionTriggerPercentage = 40;

  // Add custom ease
  const customEaseOut = CustomEase.create('custom', '0.75, 0, 0.25, 1');

  // Get HTMLElements
  const imageElement = el.querySelector('.image > img') as HTMLElement;
  const titleSpanElement = el.querySelector(
    '.caption > .title > span',
  ) as HTMLElement;
  const locationSpanElement = el.querySelector(
    '.caption > .location > span',
  ) as HTMLElement;

  // Add elements to arrays
  imageElements.value.push(el);
  imageElements.value.push(imageElement);
  captionElements.value.push(titleSpanElement);
  captionElements.value.push(locationSpanElement);

  // Return if elements are not found
  if (!imageElement || !titleSpanElement || !locationSpanElement) return;

  // Hide title and location
  gsap.set([titleSpanElement, locationSpanElement], { y: '100%' });

  // Calculate project growth
  const viewportWidth = window.innerWidth;
  const projectStartWidth = viewportWidth * projectStartTransform.width;
  const projectEndWidth = el.getBoundingClientRect().width;
  const projectGrowth = projectEndWidth - projectStartWidth;

  // Initialize image timeline
  const imageTimeline = gsap.timeline({
    paused: true,
    scrollTrigger: {
      trigger: el,
      containerAnimation: masterTl,
      scrub: 0.25,
      start: () =>
        `left+=${(projectGrowth - calcShift(index, projectEndWidth)) * index} right`,
      end: () =>
        `left+=${(projectGrowth - calcShift(index, projectEndWidth)) * index} left`,
    },
  });

  imageTimelines.value.push(imageTimeline);

  // Animation: Scale actual image asset
  imageTimeline.from(
    imageElement,
    {
      scale: projectStartTransform.scale,
    },
    0,
  );

  // Animation: Resize image wrapper
  imageTimeline.from(
    el,
    {
      width: `${projectStartTransform.width * 100}vw`,
      height: `${projectStartTransform.height * 100}vh`,
      y: `${calculateYOffset(index, yOffsetPercentage)}%`,
    },
    0,
  );

  // Initialize caption timeline
  const captionTimeline = gsap.timeline({
    paused: true,
    scrollTrigger: {
      trigger: el,
      containerAnimation: masterTl,
      start: () =>
        `left+=${projectGrowth * index} ${captionTriggerPercentage}%`,
      end: () => `left+=${projectGrowth * index} 0%`,
      toggleActions: 'play none none reverse',
      onEnter: () => {
        // Forward animation with stagger
        gsap.to([titleSpanElement, locationSpanElement], {
          y: '0%',
          duration: 0.5,
          ease: customEaseOut,
          stagger: 0.25,
          overwrite: true,
        });
      },
      onLeaveBack: () => {
        // Reverse animation without stagger
        gsap.to([titleSpanElement, locationSpanElement], {
          y: '100%',
          duration: 0.5,
          ease: customEaseOut,
          stagger: 0,
          overwrite: true,
        });
      },
    },
  });

  captionTimelines.value.push(captionTimeline);
}
</script>

<style scoped lang="scss">
.teaser-featured-projects {
  // Settings
  --container-width: 200rem; // 200 * 16px = 3200px
  --project-gap: 2rem;
  --total-projects: 3; // Is set in DatoCMS to be always 3
  --title-width: 70%;

  display: flex;

  @media (--vs) {
    padding-inline: var(--base-component-padding-inline);
    padding-bottom: 2.5rem;
    flex-direction: column;
    gap: 2rem;
    align-items: flex-end;
  }

  @media (--vl) {
    gap: 3rem;
    align-items: flex-end;
    padding-top: var(--margin-top);
    padding-right: 10.75rem;
    padding-bottom: 2rem;
  }
}

.teaser-featured-projects > .projects {
  @media (--vs) {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
  }

  @media (--vl) {
    display: flex;
    gap: var(--project-gap);
    height: 100%;
    width: var(--container-width);
  }
}

.teaser-featured-projects > .projects > .project {
  border: none;

  @media (--vl) {
    // Calculate width of each project
    width: calc(
      (
          var(--container-width) - var(--project-gap) *
            (var(--total-projects) - 1)
        ) / var(--total-projects)
    );
    height: 100%;
    align-self: flex-end;
  }
}

.teaser-featured-projects > .projects > .project > figure {
  @media (--vs) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  @media (--vl) {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    justify-content: flex-end;
  }
}

.teaser-featured-projects > .projects > .project > figure > .image {
  :deep(img) {
    height: 100% !important;
    max-width: 100% !important;
    object-fit: cover;
  }

  @media (--vl) {
    height: 90%;
    overflow: hidden;
  }
}

.teaser-featured-projects > .projects > .project > figure > .caption {
  @media (--vs) {
    display: flex;
    flex-direction: column-reverse;
    gap: 0.5rem;
  }

  @media (--vl) {
    display: flex;
    justify-content: space-between;
    height: 10%;
  }
}

.teaser-featured-projects > .projects > .project > figure > .caption > .title {
  @media (--vl) {
    width: var(--title-width);
    overflow: hidden;
    align-self: flex-start;

    > span {
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      line-clamp: 1;
    }
  }
}

.teaser-featured-projects
  > .projects
  > .project
  > figure
  > .caption
  > .location {
  @media (--vl) {
    flex-shrink: 0;
    max-width: calc(100% - var(--title-width));
    text-align: right;
    overflow: hidden;
    align-self: flex-start;

    > span {
      display: block;
    }
  }
}

.teaser-featured-projects > .cta {
  @media (--vl) {
    height: calc(10% + 1rem);
    padding-top: 0.75rem;
    flex-shrink: 0;
  }
}

.teaser-featured-projects > .cta > .link {
  @media (--vl) {
    border-bottom: 1.5px solid currentColor;

    > .arrow {
      display: none;
    }
  }
}
</style>
